import { Auth } from 'aws-amplify'
import cn from 'classnames'
import { isArray } from 'lodash'
import mixpanel from 'mixpanel-browser'
import type { GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import qs from 'qs'
import React, { useEffect, useRef, useState } from 'react'
import { decodeToken } from 'react-jwt'
import { useDispatch } from 'react-redux'
import { Dispatch } from 'redux'
import { FormFieldInput } from 'src/components/global/ListingForm/FormFieldInput/FormFieldInput'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import SkeletonLoading from 'src/components/SkeletonLoading/SkeletonLoading'
// import { Header } from 'src/components/Header/Header'
import {
  AMPLIFY_USER_POOL_API_CLIENT_ID,
  COGNITO_OAUTH_DOMAIN,
  MIXPANEL_ENABLED,
  SITE_URL,
  THEME,
} from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
// import { useGetPremiumOrganizationsByDomainEmailQuery } from 'src/graphql/generated/hooks'
import { setAuthToLink } from 'src/hooks/useApolloClient'
import { Meta } from 'src/layout/Meta'
import { setAuth } from 'src/store/actions/auth'
import { showToast } from 'src/store/actions/toastAction'
import { Main } from 'src/templates/Main'

const Header = dynamic(() => import('src/components/Header/Header').then((mod) => mod.Header), {
  suspense: true,
  loading: () => <SkeletonLoading className="bg-navy" />,
})

const SignIn: NextPage = () => {
  const router = useRouter()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch: Dispatch<any> = useDispatch()
  const { isReady } = router
  const searchParams = useSearchParams()
  const passedEmail = searchParams.get('email') || ''

  const [email, setEmail] = useState(passedEmail)
  const [pwd, setPwd] = useState('')
  const [msg, setMsg] = useState('')
  const [compId, setCompId] = useState('')
  const [showSSO, setShowSSO] = useState(false)
  const [isProgressing, setIsProgressing] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const title = 'SignIn - Legaltech Hub'

  useEffect(() => {
    inputRef?.current?.focus()

    setEmail(passedEmail)
  }, [passedEmail])

  const handleOnLogin = async () => {
    setIsProgressing(true)
    const date = new Date()
    const dateTime = date.toISOString().split('T')[0] + ' ' + date.toUTCString().split(' ')[4]
    try {
      const res = await Auth.signIn(email.toLocaleLowerCase(), pwd)

      if (res) {
        setIsProgressing(false)
        if (email) {
          if (MIXPANEL_ENABLED) mixpanel.identify(email)

          if (window.faitracker) {
            window.faitracker.call('track', '$form_submitted', {
              $email: email,
              $subject: `Login`,
              $sso_login: 'No',
              $is_success: 'Yes',
              $date_time: dateTime.toString(),
            })
          }
        }

        const res = await Auth.currentAuthenticatedUser()
        setAuthToLink(res.signInUserSession.idToken.jwtToken)
        dispatch(
          setAuth({
            email: res?.attributes?.email || res?.username,
            username: res.username,
            idToken: res.signInUserSession.idToken.jwtToken,
          }),
        )

        const page = (router.query['page'] as string) || (router.query['redirect'] as string) || ''
        if (page === '/compare') {
          const params = router.query['from']
          router.push({ pathname: page, query: { vendors: params } })
        } else {
          if (page === '') {
            router.push({ pathname: '/' })
          } else {
            // const params = router.query['params']
            // router.push({ pathname: page, query: JSON.parse((params as string) || '') })
            window.location.href = page
          }
        }
      } else {
        if (window.faitracker) {
          window.faitracker.call('track', '$form_submitted', {
            $email: email,
            $subject: `Login`,
            $sso_login: 'No',
            $is_success: 'No',
            $date_time: dateTime.toString(),
          })
        }
        setMsg('Invalid email or password')
        setPwd('')
        setIsProgressing(false)
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (window.faitracker) {
        window.faitracker.call('track', '$form_submitted', {
          $email: email,
          $subject: `Login`,
          $sso_login: 'No',
          $is_success: 'No',
          $date_time: dateTime.toString(),
        })
      }
      dispatch(showToast(error?.message, 'error'))
      setIsProgressing(false)
    }
  }

  const handleShowSSO = () => {
    setShowSSO(true)
  }

  const handleHiddenSSO = () => {
    setShowSSO(false)
  }

  const handleSSO = async () => {
    setIsProgressing(true)
    const { premiumOrganizations } =
      (await apolloSdk.getPremiumOrganizationsByDomainEmail({ domainEmail: compId })) || []
    if (isArray(premiumOrganizations?.data)) {
      if (premiumOrganizations?.data.length == 0) {
        dispatch(
          showToast(
            'The domain that you`ve entered doesn`t match a registered account. Please try again or contact us at info@legaltechnology.com for assistance.',
            'error',
          ),
        )
        setIsProgressing(false)
        return
      }
    } else {
      dispatch(showToast('Network Error', 'error'))
      return
    }

    try {
      const query = qs.stringify({
        redirect_uri: `${SITE_URL}/api/sso/callback/`,
        response_type: 'code',
        client_id: AMPLIFY_USER_POOL_API_CLIENT_ID,
        identity_provider: compId,
        scope: 'email openid',
        state: router.asPath,
      })

      window.location.replace(`https://${COGNITO_OAUTH_DOMAIN}/oauth2/authorize?${query}`)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      dispatch(showToast(error?.message, 'error'))
    } finally {
      setIsProgressing(false)
    }
  }

  useEffect(() => {
    if (!isReady) return

    const checkSignIn = async () => {
      const _isSSO = localStorage.getItem('sso') === 'true'

      if (!_isSSO) {
        const res = await Auth.currentAuthenticatedUser()
        if (res) {
          const page = (router.query['page'] as string) || (router.query['redirect'] as string) || ''
          if (page === '/compare') {
            const params = router.query['from']
            router.push({ pathname: page, query: { vendors: params } })
          } else {
            if (page === '') {
              router.push({ pathname: '/' })
            } else {
              // const params = router.query['params']
              // router.push({ pathname: page, query: JSON.parse((params as string) || '') })
              router.push(page)
            }
          }
        }
      } else {
        const _token = localStorage.getItem('idToken')
        if (_token && _token !== undefined && _token !== 'undefined') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const _token_decode = decodeToken(_token as string) as any

          if (_token_decode.exp > Date.now() / 1000 - 300) {
            router.push({ pathname: '/' })
          }
        }
      }
    }

    checkSignIn()
  }, [isReady])

  useEffect(() => {
    if (router.query['company']) {
      setCompId(router.query['company'] as string)
      setShowSSO(true)
    }
  }, [router.query])

  return (
    <Main meta={<Meta title={title} description={title} url="/signin" />}>
      <ThemeOnly theme="legaltech">
        <Header />
      </ThemeOnly>
      <div id="mainContainer" className="mainContianer h-full w-full">
        <div className="w-full body-height">
          <div className="flex w-full h-full justify-center bg-clouds p-[10px]">
            {showSSO && (
              <form
                className="w-full md:w-[unset]"
                onSubmit={(e: React.SyntheticEvent) => {
                  e.preventDefault()
                  handleSSO()
                }}
              >
                <div className="flex flex-col rounded-[8px] bg-white py-[33px] px-[40px] md:w-[521px] sm:w-full mt-[79px]">
                  <h1 className="text-[30px] font-bold font-secondary text-[#202A35]">Single Sign-On</h1>
                  <div className="flex flex-col">
                    <div className="w-full text-[14px] font-secondary font-[500] font-regular text-darkgray mt-[20px]">
                      <p>Company Domain *</p>
                      <FormFieldInput
                        id="compId"
                        type="text"
                        value={compId}
                        required={true}
                        onChange={(val) => setCompId(val.toLowerCase())}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row mt-[20px] md:items-center">
                    <button
                      className={cn(
                        'text-white font-secondary text-[14px] font-bold text-center rounded-[22.5px] px-[25px] bg-navy h-[45px] w-full mt-5 md:mt-0 md:w-[152px] md:ml-auto',
                        isProgressing ? 'opacity-40' : '',
                      )}
                      type="submit"
                      disabled={isProgressing}
                    >
                      Sign In
                    </button>
                  </div>

                  <div className="flex flex-row mt-[20px]">
                    <Link
                      onClick={handleHiddenSSO}
                      className="text-[16px] font-primary font-bold text-textdark underline cursor-pointer"
                      href={''}
                    >
                      &lt; Back to password
                    </Link>
                  </div>
                </div>
              </form>
            )}

            {!showSSO && (
              <form
                className="w-full md:w-[unset]"
                onSubmit={(e: React.SyntheticEvent) => {
                  e.preventDefault()
                  handleOnLogin()
                }}
              >
                <div className="flex flex-col rounded-[8px] bg-white py-[33px] px-[40px] md:w-[521px] sm:w-full mt-[79px]">
                  <h1 className="text-[30px] font-bold font-secondary text-[#202A35]">Sign In</h1>
                  {!!msg && (
                    <div className="w-full text-[14px] font-secondary font-[500] font-regular text-[#e11d48] mt-[20px]">
                      <p>{msg}</p>
                    </div>
                  )}
                  <div className="flex flex-col">
                    <div className="w-full text-[14px] font-secondary font-[500] font-regular text-darkgray mt-[20px]">
                      <p>Email *</p>
                      <FormFieldInput
                        setref={inputRef}
                        id="email"
                        type="email"
                        value={email}
                        required={true}
                        onChange={setEmail}
                      />
                    </div>
                    <div className="w-full text-[14px] font-secondary font-regular font-[500] text-darkgray mt-[20px]">
                      <p>Password *</p>
                      <FormFieldInput id="password" type="password" required={true} value={pwd} onChange={setPwd} />
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row mt-[20px] md:items-center">
                    {!isProgressing ? (
                      <Link
                        href="/forgot"
                        className="text-[16px] font-primary font-bold text-textdark underline cursor-pointer pr-[10px]"
                      >
                        Forgot Password
                      </Link>
                    ) : (
                      <a className="text-[16px] font-primary font-bold text-textdark underline cursor-pointer pr-[10px]">
                        Forgot Password
                      </a>
                    )}

                    <button
                      className={cn(
                        'text-white font-secondary text-[14px] font-bold text-center rounded-[22.5px] px-[25px] bg-navy h-[45px] w-full mt-5 md:mt-0 md:w-[152px] md:ml-auto',
                        isProgressing ? 'opacity-40' : '',
                      )}
                      type="submit"
                      disabled={isProgressing}
                    >
                      Sign In
                    </button>
                  </div>

                  <div className="flex flex-row mt-[20px]">
                    <span className="text-[16px] font-primary text-textdark pr-[5px]">Don't have an account?</span>
                    {!isProgressing ? (
                      <Link
                        href={'/registration'}
                        className="text-[16px] font-primary font-bold text-textdark underline cursor-pointer"
                      >
                        Sign Up
                      </Link>
                    ) : (
                      <a className="text-[16px] font-primary font-bold text-textdark underline cursor-pointer">
                        Sign Up
                      </a>
                    )}
                  </div>

                  <hr className="my-6" />

                  <button
                    onClick={handleShowSSO}
                    className="text-white font-secondary text-[14px] font-bold text-center rounded-[22.5px] px-[25px] bg-base h-[45px] w-full"
                  >
                    Single Sign-On
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </Main>
  )
}

export default SignIn

export const getStaticProps: GetStaticProps = () => {
  if (THEME === 'ilta')
    return {
      revalidate: 60,
      notFound: true,
    }

  return {
    props: {},
    revalidate: 60,
  }
}
